/**
 * Treemap label tier from RENDERED size (F4, plan §3.3 · §4.5).
 *
 * Four tiers, because the treemap has to stay readable from a full-bleed
 * Technology tile down to a sliver of Real Estate. Height thresholds are in
 * RENDERED PIXELS (demo line 870, plan §4.5). `labelTier` is the plan-pinned
 * height ladder plus the exclusive 15% tier-3 width rule; production calls
 * `labelTierForRect`, which then clamps by the §4.5 pixel-width floors so a
 * tall sliver cannot inherit large type.
 *
 *   3 — name + return + meta line
 *   2 — name + return, larger type
 *   1 — name + return
 *   0 — name only
 */
export const LABEL_TIER_MIN_HEIGHT_PX = Object.freeze({ 1: 34, 2: 58, 3: 100 });
/**
 * Inclusive pixel floors for every tier (plan §4.5). Height can earn a dense
 * label on a tall sliver; these floors drop the type to what the rendered
 * WIDTH can actually hold. Derived from the type that tier paints (name-sm ~3
 * glyphs, "+99.9%" at name-lg, meta ~18ch) plus tile padding.
 */
export const LABEL_TIER_MIN_WIDTH_PX = Object.freeze({ 1: 40, 2: 64, 3: 112 });
/**
 * Tier-3 width threshold. Despite the MIN name, the comparison is STRICT
 * (`widthPct > …`): the Phase 0 demo and `layout.test.ts` pin exclusive
 * behaviour so a tile at exactly 15% stays tier 2. Renaming would break the
 * exported constant; the exclusive comparison is the load-bearing contract.
 */
export const LABEL_TIER_3_MIN_WIDTH_PCT = 15;

export type LabelTier = 0 | 1 | 2 | 3;

export function labelTier(heightPx: number, widthPct: number): LabelTier {
  if (heightPx >= LABEL_TIER_MIN_HEIGHT_PX[3] && widthPct > LABEL_TIER_3_MIN_WIDTH_PCT) return 3;
  if (heightPx >= LABEL_TIER_MIN_HEIGHT_PX[2]) return 2;
  if (heightPx >= LABEL_TIER_MIN_HEIGHT_PX[1]) return 1;
  return 0;
}

function atMost(tier: LabelTier, cap: LabelTier): LabelTier {
  return tier <= cap ? tier : cap;
}

function clampLabelTier(tier: LabelTier, widthPx: number): LabelTier {
  if (widthPx < LABEL_TIER_MIN_WIDTH_PX[1]) return 0;
  if (widthPx < LABEL_TIER_MIN_WIDTH_PX[2]) return atMost(tier, 1);
  if (widthPx < LABEL_TIER_MIN_WIDTH_PX[3]) return atMost(tier, 2);
  return tier;
}

/** Height ladder + 15% rule, then the pixel-width clamp. Production uses this. */
export function labelTierForRect(
  heightPx: number,
  widthPx: number,
  containerWidth: number,
): LabelTier {
  const widthPct = containerWidth > 0 ? (widthPx / containerWidth) * 100 : 0;
  return clampLabelTier(labelTier(heightPx, widthPct), widthPx);
}
